import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Download, Check, Calendar, Clock, FileCheck, History } from 'lucide-react';
import Layout from '../../../layouts/MainLayout';
import KPICard from '../../../components/KPICard';
import PieChart from '../../../components/charts/PieChart';
import { getSnapshots, getEvents, getObjections } from '../../../api';
import FilingDetailsSummary from '../components/FilingDetailsSummary';
const FilingDetails = () => {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [events, setEvents] = useState([]);
  const [objectionRows, setObjectionRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [snapRes, eventRes, objectionRes] = await Promise.all([getSnapshots({
          tracking_number: id,
          limit: 1
        }), getEvents({
          tracking_number: id
        }), getObjections({
          tracking_number: id
        })]);
        const snapPayload = snapRes?.data?.data;
        if (Array.isArray(snapPayload)) {
          setSnapshot(snapPayload.length > 0 ? snapPayload[0] : null);
        } else if (snapPayload && typeof snapPayload === 'object') {
          setSnapshot(snapPayload);
        } else {
          setSnapshot(null);
        }
        setEvents(eventRes.data?.data || []);
        setObjectionRows(Array.isArray(objectionRes?.data?.data) ? objectionRes.data.data : []);
      } catch (err) {
        console.error('Error fetching filing details:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);
  const getStatusStyle = status => {
    if (!status) return { background: 'var(--c-white)', color: 'var(--text2)' };
    const s = status.toLowerCase();
    
    // 1. Completed States -> Timeline Emerald
    if (s.includes('approved') || s.includes('accepted')) {
      return { background: 'var(--bg-teal-soft)', color: 'var(--c-teal-active)' };
    }
    // 2. Attention/Negative States -> Timeline Orange
    if (s.includes('objection') || s.includes('info') || s.includes('question') || s.includes('reject') || s.includes('disapproved') || s.includes('withdrawn') || s.includes('closed')) {
      return { background: 'var(--bg-orange-soft)', color: 'var(--c-orange)' };
    }
    // 3. Active/In-Progress States -> Timeline Purple
    if (s.includes('review') || s.includes('pending')) {
      return { background: 'var(--bg-purple-soft)', color: 'var(--c-purple-1)' };
    }
    // 4. Initial States -> Timeline Blue
    if (s.includes('assigned') || s.includes('submitted') || s.includes('created')) {
      return { background: 'var(--bg-blue-soft)', color: 'var(--c-blue-1)' };
    }
    
    return { background: 'var(--bg-blue-soft)', color: 'var(--c-blue-1)' }; // Fallback to Blue
  };
  const formatDate = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString();
  };
  const hasObjectionSignalFromEvents = events.some((e) => {
    const text = `${e?.change_type || ''} ${e?.description || ''}`.toLowerCase();
    return text.includes('objection');
  });
  const hasObjectionSignalFromStatus = `${snapshot?.filing_status || ''} ${snapshot?.serff_status || ''} ${snapshot?.state_status || ''}`.toLowerCase().includes('objection');
  const objectionIssued = objectionRows.length > 0 || (snapshot?.correspondence_count || 0) > 0 || hasObjectionSignalFromEvents || hasObjectionSignalFromStatus;
  const milestones = [{
    label: 'Submitted',
    date: snapshot?.submission_date ? formatDate(snapshot.submission_date) : 'Pending',
    status: snapshot?.submission_date ? 'done' : 'pending',
    key: 'submitted'
  }, {
    label: 'Assigned',
    date: snapshot?.serff_status || 'Pending',
    status: snapshot?.serff_status ? 'done' : 'pending',
    key: 'assigned'
  }, {
    label: 'Objection Issued',
    date: objectionIssued ? 'Issued' : 'None',
    status: objectionIssued ? 'done' : 'pending',
    key: 'objection-issued'
  }, {
    label: 'Under Review',
    date: snapshot?.filing_status?.toLowerCase().includes('review') || snapshot?.filing_status?.toLowerCase().includes('pending') ? 'Active' : 'Pending',
    status: snapshot?.filing_status?.toLowerCase().includes('review') || snapshot?.filing_status?.toLowerCase().includes('pending') ? 'active' : 'pending',
    key: 'in-review'
  }, {
    label: 'Decision',
    date: snapshot?.disposition_date ? formatDate(snapshot.disposition_date) : 'Pending',
    status: snapshot?.disposition_date ? 'done' : 'pending',
    key: 'final-decision'
  }];
  const lastFilledIdx = milestones.reduce((max, s, i) => s.status !== 'pending' ? i : max, -1);
  if (loading) return <Layout><div className="content">Loading filing details...</div></Layout>;
  if (!snapshot) return <Layout><div className="content">Filing not found.</div></Layout>;
  const kpiCards = [{
    label: 'TOTAL DOCS',
    value: (snapshot.form_docs || 0) + (snapshot.rate_rule_docs || 0) + (snapshot.supporting_docs || 0) + (snapshot.correspondence_count || 0),
    subtitle: 'Form & supporting documents'
  }, {
    label: 'CORRESPONDENCE',
    value: snapshot.correspondence_count || 0,
    subtitle: 'Interactions & objections'
  }, {
    label: 'LAST UPDATED',
    value: formatDate(snapshot.fetched_at),
    subtitle: 'Fetched from SERFF'
  }];
  const docParts = [{
    label: 'Form',
    value: snapshot.form_docs || 0,
    color: 'var(--c-blue-1)'
  }, {
    label: 'Rate/Rule',
    value: snapshot.rate_rule_docs || 0,
    color: 'var(--c-aqua-mass)'
  }, {
    label: 'Supporting',
    value: snapshot.supporting_docs || 0,
    color: 'var(--c-purple-1)'
  }, {
    label: 'Correspondence',
    value: snapshot.correspondence_count || 0,
    color: 'var(--c-orange)'
  }];
  const docTotal = Math.max(docParts.reduce((sum, p) => sum + p.value, 0), 1);
  return <Layout>
    <div className="filing-details-page">
    <div className="topbar">
      <div className="topbar-left">
        <button onClick={() => {
          sessionStorage.setItem('comply_active_filings_tab', 'Table');
          navigate(-1);
        }} className="link-back">
          <ChevronLeft size={14} /> Back to Filings
        </button>
        <div className="topbar-title">{snapshot.serff_tracking_number}</div>
        <div className="topbar-sub">{snapshot.company_name}</div>
      </div>
      <div className="topbar-right">
        <button className="btn btn-sm btn-filters">
          <Download size={16} /> Download
        </button>
      </div>
    </div>

    <div className="content">
      <div className="kpi-banner-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 0 }}>
        {kpiCards.map((kpi, idx) => (
          <KPICard
            key={kpi.label}
            title={kpi.label}
            value={kpi.value}
            subtitle={kpi.subtitle}
            staggerIndex={idx}
          />
        ))}
      </div>

      <div className="report-card">
        <div className="report-card-title">Filing Information</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>State</div>
            <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--c-black)' }}>{snapshot.state || '—'}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Filing Type</div>
            <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--c-black)' }}>{snapshot.filing_type || '—'}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', gridColumn: 'span 2' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>TOI & Sub-TOI</div>
            <div style={{ fontSize: '13px', color: 'var(--text2)', lineHeight: 1.4 }}>
              <strong>{snapshot.toi || '—'}</strong><br />
              <span style={{ color: 'var(--text2)', fontSize: '12px' }}>{snapshot.sub_toi || '—'}</span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', gridColumn: 'span 1' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Product Name</div>
            <div style={{ fontSize: '14px', color: 'var(--c-black)' }}>{snapshot.product_name || '—'}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>SERFF Status</div>
            <div><span className="pill" style={getStatusStyle(snapshot.serff_status)}>{snapshot.serff_status || '—'}</span></div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '16px' }}>
        <div className="report-card">
          <div className="report-card-title">Timeline</div>
          <div style={{ position: 'relative', height: '100%' }}>
            {/* Metrics display preserved without connecting threads */}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: '78px', columnGap: '0px', position: 'relative', zIndex: 1 }}>
              {/* 1. Top Left: Submission */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'var(--bg-blue-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--c-blue-1)', marginBottom: '8px', boxShadow: '0 0 0 4px var(--c-white)' }}>
                  <Calendar size={22} />
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text2)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.02em' }}>Submission</div>
                  <div style={{ fontSize: '14px', color: 'var(--c-black)', marginTop: '2px' }}>{formatDate(snapshot.submission_date)}</div>
                </div>
              </div>

              {/* 2. Top Right: Disposition */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'var(--bg-teal-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--c-teal-active)', marginBottom: '8px', boxShadow: '0 0 0 4px var(--c-white)' }}>
                  <FileCheck size={22} />
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text2)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.02em' }}>Disposition</div>
                  <div style={{ fontSize: '14px', color: 'var(--c-black)', marginTop: '2px' }}>{formatDate(snapshot.disposition_date)}</div>
                </div>
              </div>

              {/* 3. Bottom Left: Status Change */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'var(--bg-orange-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--c-orange)', marginBottom: '8px', boxShadow: '0 0 0 4px var(--c-white)' }}>
                  <Clock size={22} />
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text2)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.02em' }}>Last Status</div>
                  <div style={{ fontSize: '14px', color: 'var(--c-black)', marginTop: '2px' }}>{formatDate(snapshot.state_status_last_changed)}</div>
                </div>
              </div>

              {/* 4. Bottom Right: First Tracked */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'var(--bg-purple-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--c-purple-1)', marginBottom: '8px', boxShadow: '0 0 0 4px var(--c-white)' }}>
                  <History size={22} />
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text2)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.02em' }}>First Tracked</div>
                  <div style={{ fontSize: '14px', color: 'var(--c-black)', marginTop: '2px' }}>{formatDate(snapshot.fetched_at)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="report-card">
          <div className="report-card-title">Document Mix</div>
          <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: '14px', alignItems: 'center' }}>
            <PieChart data={docParts} label="Docs" animate />
            <div style={{ display: 'grid', gap: '8px' }}>
              {docParts.map((p) => (
                <div key={p.label} style={{ display: 'grid', gridTemplateColumns: '10px 1fr auto auto', gap: '8px', alignItems: 'center' }}>
                  <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: p.color }} />
                  <span style={{ fontSize: '12px', color: 'var(--text2)' }}>{p.label}</span>
                  <span style={{ fontSize: '11px', color: 'var(--text2)' }}>{Math.round((p.value / docTotal) * 100)}%</span>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--c-black)' }}>{p.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="report-card">
        <div className="report-card-title">Filing Milestones</div>
        <div className="milestone-track">
          <div className="milestone-track__line"></div>
          {milestones.map((step, idx) => {
            const isForcedDone = idx < lastFilledIdx;
            const isDone = isForcedDone || step.status === 'done';
            const isActive = step.status === 'active';
            const isPending = !isDone && !isActive;
            
            // Only use Orange if this is the objection step AND it is NATIVELY done (truly issued).
            // If it's waterfall-forced through, keep it Green (passed cleanly).
            const stepColor = (step.key === 'objection-issued' && step.status === 'done') 
              ? 'var(--c-orange)' 
              : 'var(--c-teal-active)';
            
            return (
              <div key={idx} className="milestone-step">
                <div 
                  className={`milestone-step__dot ${isPending ? 'pending' : step.key}`}
                  style={!isPending ? {
                    borderColor: stepColor,
                    background: 'var(--c-white)',
                    color: stepColor,
                    boxShadow: `0 2px 4px ${stepColor}20`
                  } : {}}
                >
                  {isDone ? <Check size={18} strokeWidth={3} /> : idx + 1}
                </div>
                <div 
                  className={`milestone-step__label ${isPending ? 'pending' : step.key}`}
                  style={!isPending ? { color: stepColor } : {}}
                >
                  {step.label}
                </div>
                <div className="milestone-step__date">{step.date || '—'}</div>
              </div>
            );
          })}
        </div>
      </div>

      <FilingDetailsSummary summary={snapshot.summary} />

      {/* {objectionRows.length > 0 ? (
        <div className="report-card" style={{ border: '1px solid var(--border)', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>
          <div className="report-card-title" style={{ marginBottom: '12px' }}>Objections & responses</div>
          {objectionRows.every((row) => !pickStr(row, ['objection_text', 'related_objection_text', 'response_text', 'changed_items_text'])) ? (
            <div style={{
              fontSize: '12px',
              color: 'var(--text2)',
              lineHeight: 1.5,
              padding: '10px 12px',
              background: 'var(--bg-orange-soft)',
              border: '1px solid var(--orange-bg)',
              borderRadius: '8px',
              marginBottom: '12px'
            }}>
              This filing has objection rows in the database, but the API did not return any objection or response text (often because an older backend only returned the tracking id per row). Restart <strong>market-intelligence-backend</strong> after updating so <code style={{ fontSize: '11px' }}>/api/objections?tracking_number=â€¦</code> returns full records.
            </div>
          ) : null}
          <div style={{ display: 'grid', gap: '12px' }}>
            {objectionRows.map((row, idx) => {
              const objectionNum = row.objection_number ?? row.objectionNumber;
              const letterIx = row.letter_index ?? row.letterIndex;
              const label = objectionNum != null
                ? (letterIx != null ? `Letter ${letterIx} · Objection ${objectionNum}` : `Objection ${objectionNum}`)
                : `Item ${idx + 1}`;
              const objectionBody = pickStr(row, ['objection_text']) || pickStr(row, ['related_objection_text']);
              const responseBody = pickStr(row, ['response_text']);
              const changedBody = pickStr(row, ['changed_items_text']);
              return (
                <div
                  key={row.id ?? `${letterIx ?? 'x'}-${objectionNum ?? idx}-${idx}`}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: '10px',
                    padding: '12px 14px',
                    background: 'var(--c-white)'
                  }}
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px', alignItems: 'baseline', marginBottom: '10px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--c-black)' }}>{label}</span>
                    {(row.objection_letter_date || row.objectionLetterDate) ? (
                      <span style={{ fontSize: '11px', color: 'var(--text2)' }}>Letter {formatDate(row.objection_letter_date || row.objectionLetterDate)}</span>
                    ) : null}
                    {(row.respond_by_date || row.respondByDate) ? (
                      <span style={{ fontSize: '11px', color: 'var(--text2)' }}>Respond by {formatDate(row.respond_by_date || row.respondByDate)}</span>
                    ) : null}
                    {(row.response_submitted_date || row.responseSubmittedDate) ? (
                      <span style={{ fontSize: '11px', color: 'var(--text2)' }}>Response {formatDate(row.response_submitted_date || row.responseSubmittedDate)}</span>
                    ) : null}
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                    Objection
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: 'var(--text2)',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    maxHeight: '200px',
                    overflow: 'auto',
                    marginBottom: '12px'
                  }}>
                    {objectionBody || '—'}
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                    Response
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: 'var(--text2)',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    maxHeight: '200px',
                    overflow: 'auto',
                    marginBottom: changedBody && !responseBody ? '0' : '12px'
                  }}>
                    {responseBody || '—'}
                  </div>
                  {changedBody ? (
                    <>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px', marginTop: responseBody ? '4px' : '0' }}>
                        Schedule / item changes
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: 'var(--text2)',
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                        maxHeight: '200px',
                        overflow: 'auto'
                      }}>
                        {changedBody}
                      </div>
                    </>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null} */}

      {/* <div className="report-card" style={{ border: '1px solid var(--border)', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>
        <div className="report-card-title" style={{ marginBottom: '12px' }}>Change Timeline</div>
        <div className="enhanced-timeline enhanced-timeline" style={{ display: 'grid', gap: '10px' }}>
          {events.length > 0 ? (
            <>
              <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '10px 12px', background: 'var(--c-white)' }}>
                <div style={{ fontSize: '11px', color: 'var(--text2)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Event Mix</div>
                <div style={{ display: 'flex', height: '8px', borderRadius: '999px', overflow: 'hidden', background: 'var(--border)', marginBottom: '8px' }}>
                  {eventTypeSeries.map((item) => (
                    <div key={item.label} style={{ width: `${(item.value / eventTotal) * 100}%`, background: item.color }} />
                  ))}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 12px' }}>
                  {eventTypeSeries.map((item) => (
                    <div key={`legend-${item.label}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text2)' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '999px', background: item.color }} />
                      <span>{item.label}</span>
                      <strong style={{ color: 'var(--c-black)' }}>{item.value}</strong>
                    </div>
                  ))}
                </div>
              </div>
              {events.map((event) => (
                <div key={event.id} style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '10px 12px', display: 'grid', gap: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '999px', background: getEventColor(event.change_type), flexShrink: 0 }} />
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--c-black)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {formatEventType(event.change_type)}
                      </span>
                    </div>
                    <span style={{ fontSize: '11px', color: 'var(--text2)', flexShrink: 0 }}>
                      {formatDate(event.detected_at)} {new Date(event.detected_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text2)', lineHeight: 1.45 }}>
                    {event.change_type === 'status_change'
                      ? <>Status changed from <strong style={{ color: 'var(--c-black)' }}>{event.old_value || 'None'}</strong> to <strong style={{ color: 'var(--c-black)' }}>{event.new_value || '—'}</strong>.</>
                      : <>Detected change in <strong style={{ color: 'var(--c-black)' }}>{event.change_type?.replace(/_/g, ' ')}</strong>{event.new_value ? `: ${event.new_value}` : '.'}</>}
                  </div>
                </div>
              ))}
            </>
          ) : <div className="page-empty"><Activity size={48} color="var(--text3)" className="page-empty__icon" /><p className="page-empty__text">No audit history available for this filing.</p></div>}
        </div>
      </div> */}
    </div>
    </div>
  </Layout>;
};
export default FilingDetails;
